'use client';

import { createElement, useEffect, useRef, useState } from 'react';

import { useRouter } from 'next/navigation';

import { getHighlightedRaffleDetailsById } from '@/actions';
import { useWhitelabelConfig } from '@/hooks/useWhitelabelConfig';
import { toast } from '@/libs';
import {
  useCheckoutStore,
  useCustomerStore,
  useDrawerStore,
  useModalStore,
} from '@/stores';
import { ROUTES } from '@/types/enums';
import { ValidationKey } from '@/types/enums/WhitelabelConfig';
import type { HighlightedRaffleDetails } from '@/types/models';
import { calculatePackTotal, logger, normalizePackOffers } from '@/utils';

import type { RecoveryCustomerData } from './useRecoveryCpfForm';
import type { RegisteredUserData, RegistrationSeed } from './useUserRegisterForm';

import { EditUserProfileModal, RegistrationExitConfirmation } from '../components';
import { RECOVERY_MESSAGES } from '../constants';
import { UserDrawer } from '../UserDrawer';

type UserDataType = {
  name: string;
  socialName?: string;
  document: string;
  phone: string;
  customerId: string;
  hasAccount: boolean;
  hasMail: boolean;
  hasAddress: boolean;
};

const userDrawerConfig = {
  title: 'Minha Conta',
  Component: UserDrawer,
  drawerType: 'user-account',
  drawerHeaderOptions: { className: 'text-center pt-2' },
  onPreventClose: () => createElement(RegistrationExitConfirmation),
};

type RaffleWithPromotionFields = {
  prizeValue?: number;
  isPromotionAvailable?: boolean;
  promotionPercentage?: number;
  promotionTicketsAmount?: number;
  promotionTitle?: string;
  promotionDescription?: string;
};

type PurchaseSummary = {
  ticketQuantity: number;
  ticketPrice: number;
  totalValue: number;
};

export type UserDrawerStep =
  | 'login'
  | 'register'
  | 'address'
  | 'profile'
  | 'recovery-cpf'
  | 'recovery-kyc'
  | 'recovery-validating'
  | 'recovery-phone'
  | 'recovery-success'
  | 'recovery-error';

export const calculatePurchaseSummary = (
  raffleDetails:
    | HighlightedRaffleDetails
    | (HighlightedRaffleDetails & {
        promotionPacks: unknown;
      }),
  selectedTicketsAmount?: number,
): PurchaseSummary => {
  const ticketQuantity =
    selectedTicketsAmount ??
    raffleDetails.initialTicketsAmount ??
    raffleDetails.minTicketsPerPurchase ??
    1;
  const ticketPrice = raffleDetails.ticketPrice ?? 0;
  const legacyPacks =
    'promotionPacks' in raffleDetails ? raffleDetails.promotionPacks : undefined;
  // TODO(ILOTTO-2034): this flow only sees legacy packs; the checkout resolves unified offers.
  const offers = normalizePackOffers({
    promotionOffers: undefined,
    legacyPacks,
  });
  const totalValue = calculatePackTotal({
    offers,
    ticketsAmount: ticketQuantity,
    unitPrice: ticketPrice,
  });

  return { ticketQuantity, ticketPrice, totalValue };
};

export const useUserDrawer = () => {
  const router = useRouter();
  const openDrawer = useDrawerStore((state) => state.openDrawer);
  const closeCurrentDrawer = useDrawerStore((state) => state.closeCurrentDrawer);
  const setDrawerOpen = useDrawerStore((state) => state.setDrawerOpen);
  const replaceDrawer = useDrawerStore((state) => state.replaceDrawer);
  const drawers = useDrawerStore((state) => state.drawers);
  const openModal = useModalStore((state) => state.openModal);

  const customerData = useCustomerStore((state) => state.customerData);
  const setCustomerData = useCustomerStore((state) => state.setCustomerData);
  const clearCustomerData = useCustomerStore((state) => state.clearCustomerData);
  const checkoutData = useCheckoutStore((state) => state.checkoutData);
  const setPurchaseData = useCheckoutStore((state) => state.setPurchaseData);
  const resetCheckout = useCheckoutStore((state) => state.resetCheckout);

  const { getValue } = useWhitelabelConfig();
  const shouldSkipAddressStep =
    getValue<boolean>(ValidationKey.VALIDATION_SKIP_ADDRESS_STEP) ?? false;

  const [currentStep, setCurrentStep] = useState<UserDrawerStep>('login');
  const [userData, setUserData] = useState<UserDataType | null>(null);
  const [registrationSeed, setRegistrationSeed] = useState<RegistrationSeed | null>(null);
  // Ref, e não `currentStep` na dep do efeito abaixo: incluir a step nas deps faz
  // o efeito re-rodar a cada troca e desfazer o que handleLoginSuccess definiu.
  const isRegisteringRef = useRef(false);
  const [recoveryCustomerData, setRecoveryCustomerData] =
    useState<RecoveryCustomerData | null>(null);
  const [recoveryErrorMessage, setRecoveryErrorMessage] = useState<string | null>(null);

  const clearRecoveryState = () => {
    setRecoveryCustomerData(null);
    setRecoveryErrorMessage(null);
  };

  const openUserDrawer = () => {
    const hasOpenDrawer = drawers.length > 0;

    if (hasOpenDrawer) {
      replaceDrawer(userDrawerConfig);
    } else {
      openDrawer(userDrawerConfig);
    }
  };

  const handleLoginSuccess = async (
    data: UserDataType,
    options?: { skipCheckoutRedirect?: boolean },
  ) => {
    const socialName = data.socialName?.trim();
    const currentPurchaseData = checkoutData.purchaseData;

    setCustomerData({
      name: data.name,
      ...(socialName ? { socialName } : {}),
      document: data.document,
      phone: data.phone,
      email: customerData?.email || '',
      hasAccount: data.hasAccount,
      hasMail: data.hasMail,
      hasAddress: data.hasAddress,
      customerId: data.customerId,
    });

    if (options?.skipCheckoutRedirect || (data.hasAddress && data.hasMail)) {
      setUserData(data);
      setCurrentStep('profile');
      return;
    }

    const highlightedRaffleIdRaw = localStorage.getItem('highlightedRaffleId');
    const highlightedRaffleId =
      highlightedRaffleIdRaw && highlightedRaffleIdRaw.trim() !== ''
        ? highlightedRaffleIdRaw.trim()
        : null;

    if (!highlightedRaffleId) {
      logger.error('No highlighted raffle ID found in localStorage.');
      if (data.hasAccount && data.name) {
        setUserData(data);
        setCurrentStep('profile');
      } else {
        toast.error(
          'Nenhum sorteio em destaque encontrado. Por favor, selecione um sorteio primeiro.',
        );
        setUserData(data);
        setCurrentStep('profile');
      }
      return;
    }

    try {
      const raffleDetails = await getHighlightedRaffleDetailsById(highlightedRaffleId, {
        caller: 'handleLoginSuccess',
      });

      if (!raffleDetails) {
        toast.error('Erro ao carregar detalhes do sorteio. Tente novamente.');
        setUserData(data);
        setCurrentStep('profile');
        return;
      }

      if (!raffleDetails.id || !raffleDetails.name || !raffleDetails.drawDate) {
        logger.error('Raffle details missing required fields:', {
          id: raffleDetails.id,
          name: raffleDetails.name,
          drawDate: raffleDetails.drawDate,
        });
        toast.error('Dados do sorteio incompletos. Tente novamente.');
        setUserData(data);
        setCurrentStep('profile');
        return;
      }

      const preservedTicketsAmount =
        currentPurchaseData?.raffleId === raffleDetails.id
          ? (currentPurchaseData.ticketsAmount ?? undefined)
          : undefined;

      const { ticketQuantity, ticketPrice, totalValue } = calculatePurchaseSummary(
        raffleDetails,
        preservedTicketsAmount,
      );

      const baseData = {
        raffleId: raffleDetails.id,
        ticketsAmount: ticketQuantity,
        ticketPrice: ticketPrice,
        totalValue: totalValue,
        raffleName: raffleDetails.name || '',
        raffleTag: raffleDetails.tag || '',
        drawDate: raffleDetails.drawDate,
        minTicketsPerPurchase: raffleDetails.minTicketsPerPurchase ?? 1,
        maxTicketsPerPurchase:
          raffleDetails.maxTicketsPerPurchase ?? Number.MAX_SAFE_INTEGER,
      };

      const raffleWithPromotion = raffleDetails as HighlightedRaffleDetails &
        RaffleWithPromotionFields;

      const purchaseData = {
        ...baseData,
        prizeValue: raffleWithPromotion.prizeValue ?? undefined,
        isPromotionAvailable: raffleWithPromotion.isPromotionAvailable ?? undefined,
        promotionPercentage: raffleWithPromotion.promotionPercentage ?? undefined,
        promotionTicketsAmount: raffleWithPromotion.promotionTicketsAmount ?? undefined,
        promotionTitle: raffleWithPromotion.promotionTitle ?? undefined,
        promotionDescription: raffleWithPromotion.promotionDescription ?? undefined,
      };

      resetCheckout();
      setPurchaseData(purchaseData);
      setDrawerOpen(drawers[0]?.id || '', false);
      router.push(ROUTES.Enum['/checkout']);
    } catch (error) {
      logger.error('Error redirecting to checkout:', error);
      toast.error('Erro ao redirecionar para checkout. Tente novamente.');
      setUserData(data);
      setCurrentStep('profile');
    }
  };

  const handleNeedsRegistration = (seed: RegistrationSeed) => {
    isRegisteringRef.current = true;
    setRegistrationSeed(seed);
    setCurrentStep('register');
  };

  const handleRegisterSuccess = (data: RegisteredUserData) => {
    setRegistrationSeed(null);
    setUserData(data);

    if (!data.hasAddress && !shouldSkipAddressStep) {
      setCurrentStep('address');
      return;
    }

    isRegisteringRef.current = false;
    setCurrentStep('profile');
  };

  const handleAddressComplete = () => {
    isRegisteringRef.current = false;
    setUserData((current) => (current ? { ...current, hasAddress: true } : current));
    setCurrentStep('profile');
  };

  const handleSkipAddress = () => {
    isRegisteringRef.current = false;
    setCurrentStep('profile');
  };

  const handleCancelRegistration = () => {
    isRegisteringRef.current = false;
    setRegistrationSeed(null);
    setCurrentStep('login');
  };

  const handleLogout = () => {
    clearRecoveryState();
    clearCustomerData();
    setUserData(null);
    setCurrentStep('login');
    router.push('/');
  };

  const handleEditProfile = () => {
    closeCurrentDrawer();
    openModal({
      Component: EditUserProfileModal,
      showCloseButton: false,
      hardClose: false,
      className: 'w-screen h-screen md:w-auto md:h-auto',
      onCloseOptional: () => {
        if (useCustomerStore.getState().customerData) {
          openDrawer(userDrawerConfig);
        }
      },
    });
  };

  const handleMyTickets = () => {
    closeCurrentDrawer();
    router.push(ROUTES.Enum['/meus-titulos']);
  };

  const handleStartRecovery = () => {
    clearRecoveryState();
    setCurrentStep('recovery-cpf');
  };

  const handleRecoveryCpfSuccess = (data: RecoveryCustomerData) => {
    setRecoveryCustomerData(data);
    setRecoveryErrorMessage(null);
    setCurrentStep('recovery-kyc');
  };

  const handleKycStartValidation = () => {
    if (!recoveryCustomerData?.customerId) {
      setCurrentStep('login');
      return;
    }

    setRecoveryErrorMessage(null);
    setCurrentStep('recovery-validating');
  };

  const handleKycSuccess = () => {
    setRecoveryErrorMessage(null);
    setCurrentStep('recovery-phone');
  };

  const handleKycError = (message: string = RECOVERY_MESSAGES.KYC_FAILED) => {
    setRecoveryErrorMessage(message);
    setCurrentStep('recovery-error');
  };

  const handlePhoneUpdateSuccess = () => {
    setRecoveryErrorMessage(null);
    setCurrentStep('recovery-success');
  };

  const handleRetryKyc = () => {
    if (!recoveryCustomerData?.customerId) {
      setCurrentStep('login');
      return;
    }

    setRecoveryErrorMessage(null);
    setCurrentStep('recovery-kyc');
  };

  const handleBackToLogin = () => {
    clearRecoveryState();
    setCurrentStep('login');
  };

  useEffect(() => {
    if (isRegisteringRef.current) return;

    if (
      customerData &&
      customerData.name &&
      customerData.document &&
      customerData.phone
    ) {
      const socialName =
        customerData.socialName?.trim() ||
        customerData.pendingRegistrationData?.socialName?.trim();

      setUserData({
        ...customerData,
        customerId: customerData.customerId,
        name: customerData.name,
        ...(socialName ? { socialName } : {}),
        document: customerData.document,
        phone: customerData.phone,
      });
      setCurrentStep('profile');
    } else {
      setUserData(null);
      setCurrentStep('login');
    }
  }, [customerData]);

  return {
    openUserDrawer,
    currentStep,
    userData,
    registrationSeed,
    recoveryCustomerData,
    recoveryErrorMessage,
    handleLoginSuccess,
    handleNeedsRegistration,
    handleRegisterSuccess,
    handleAddressComplete,
    handleSkipAddress,
    handleCancelRegistration,
    handleEditProfile,
    handleMyTickets,
    handleLogout,
    handleStartRecovery,
    handleRecoveryCpfSuccess,
    handleKycStartValidation,
    handleKycSuccess,
    handleKycError,
    handlePhoneUpdateSuccess,
    handleRetryKyc,
    handleBackToLogin,
  };
};
